/**
 * Dev Mode Debug Panel
 *
 * Shows subscription state, mode, and repository info for testing.
 * Only renders in development mode.
 */

import { useState } from 'react'
import { Bug, X, ChevronDown, ChevronUp } from 'lucide-react'
import { useStudio } from '@/context/StudioContext'
import { useFamily } from '@/context/FamilyContext'

export function DevModePanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(true)

  const { mode, limits, readOnly, hasPendingMigration, isMigrating } = useStudio()
  const { family, activeWriter } = useFamily()

  // Only show in development
  if (!import.meta.env.DEV) {
    return null
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 p-2 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
        title="Open Dev Panel"
      >
        <Bug className="w-5 h-5" />
      </button>
    )
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-900 text-gray-100 rounded-lg shadow-xl overflow-hidden text-xs font-mono max-w-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-yellow-400" />
          <span className="font-semibold">Dev Panel</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-gray-700 rounded"
          >
            {isMinimized ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-gray-700 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="p-3 space-y-3">
          {/* Studio Mode */}
          <div>
            <div className="text-gray-400 mb-1">Studio Mode</div>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 rounded ${
                  mode === 'cloud'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-600 text-gray-200'
                }`}
              >
                {mode}
              </span>
              {readOnly && (
                <span className="px-2 py-0.5 rounded bg-red-600 text-white">
                  READ_ONLY
                </span>
              )}
            </div>
          </div>

          {/* Subscription */}
          <div>
            <div className="text-gray-400 mb-1">Subscription</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-gray-500">tier:</span>
              <span className={family?.subscription_tier === 'premium' ? 'text-green-400' : ''}>
                {family?.subscription_tier || 'free'}
              </span>
              <span className="text-gray-500">status:</span>
              <span
                className={
                  family?.subscription_status === 'active'
                    ? 'text-green-400'
                    : family?.subscription_status === 'lapsed'
                    ? 'text-red-400'
                    : ''
                }
              >
                {family?.subscription_status || 'null'}
              </span>
            </div>
          </div>

          {/* Migration */}
          <div>
            <div className="text-gray-400 mb-1">Migration</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-gray-500">pending:</span>
              <span className={hasPendingMigration ? 'text-yellow-400' : ''}>
                {hasPendingMigration ? 'yes' : 'no'}
              </span>
              <span className="text-gray-500">migrating:</span>
              <span className={isMigrating ? 'text-blue-400' : ''}>
                {isMigrating ? 'yes' : 'no'}
              </span>
            </div>
          </div>

          {/* Limits */}
          <div>
            <div className="text-gray-400 mb-1">Limits</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-gray-500">maxStories:</span>
              <span>{limits.maxActiveStories === Infinity ? '∞' : limits.maxActiveStories}</span>
              <span className="text-gray-500">badges:</span>
              <span className={limits.canAccessBadges ? 'text-green-400' : 'text-gray-500'}>
                {limits.canAccessBadges ? 'yes' : 'no'}
              </span>
              <span className="text-gray-500">dashboard:</span>
              <span className={limits.canAccessDashboard ? 'text-green-400' : 'text-gray-500'}>
                {limits.canAccessDashboard ? 'yes' : 'no'}
              </span>
            </div>
          </div>

          {/* Active Writer */}
          <div>
            <div className="text-gray-400 mb-1">Active Writer</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-gray-500">id:</span>
              <span className="truncate" title={activeWriter?.id}>
                {activeWriter?.id?.slice(0, 8) || 'none'}...
              </span>
              <span className="text-gray-500">name:</span>
              <span>{activeWriter?.display_name || 'none'}</span>
            </div>
          </div>

          {/* Family ID */}
          <div>
            <div className="text-gray-400 mb-1">Family</div>
            <span className="text-gray-300 truncate block" title={family?.id}>
              {family?.id?.slice(0, 8) || 'none'}...
            </span>
          </div>

          {/* Quick Actions */}
          <div className="pt-2 border-t border-gray-700">
            <div className="text-gray-400 mb-2">Quick Links</div>
            <div className="flex flex-wrap gap-2">
              <a
                href="/upgrade"
                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs"
              >
                /upgrade
              </a>
              <a
                href="/stories"
                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs"
              >
                /stories
              </a>
              <a
                href="/dashboard"
                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs"
              >
                /dashboard
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
